import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import Loading from "../../../../components/loading/loading";
import { useFormik } from "formik";
import * as yup from "yup";
import { orderUpdateRequest } from "../../../../utils/APICalls";
import { getOrderHistoryRequest } from "../../../../redux/orders/slice";
import { useDispatch } from "react-redux";

const styles = {
  icon: {
    position: "absolute",
    right: 12,
    top: 12,
    color: "rgba(0, 0, 0, 0.54)",
  },
  text: {
    color: "rgba(0, 0, 0, 0.87)",
  },
  save: {
    ml: 2,
    mr: 1,
  },
};

type FormValues = {
  type: string;
  description: string;
};

const ValidationSchema = yup.object({
  type: yup.string().required("This field can't be empty"),
  description: yup.string().required("This field can't be empty"),
});

export default function AddOrderUpdate({
  orderId,
  showDialog,
  closeDialog,
  navigateToIssues,
  orderUpdateSuccess,
}: any) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik<FormValues>({
    initialValues: {
      type: "",
      description: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const payload = {
        orderIds: [orderId],
        actionType: values.type,
        description: values.description,
      };
      orderUpdate(payload);
    },
  });

  const orderUpdate = async (payload: any) => {
    setLoading(true);
    const response = await orderUpdateRequest(payload);
    setLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderHistoryRequest({ orderId }));
      orderUpdateSuccess();
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={closeDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth={"md"}
    >
      <DialogTitle id="alert-dialog-title" sx={{ fontWeight: "510" }}>
        Order Update
      </DialogTitle>
      <IconButton aria-label="close" onClick={closeDialog} sx={styles.icon}>
        <CloseIcon />
      </IconButton>
      <Divider />
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Typography sx={{ mt: -1 }}>
            Order updates are internal only and should be used to document
            actions that have been taken to one or more orders that will be
            beneficial to other users who may work on the order. Users, clients,
            and inspectors will not be notified, and updates will only be
            visible to internal users
          </Typography>
          <Typography sx={{ mt: 2, display: "flex" }}>
            Alternatively, if you would like to notify an inspector or client,
            <Button
              variant="text"
              sx={{ fontWeight: "510", textTransform: "none", py: 0, px: 0.5 }}
              onClick={() => {
                navigateToIssues();
                closeDialog();
              }}
            >
              open an issue
            </Button>
            instead.
          </Typography>
          <Box className="user-name-layout">
            <Typography className="label-txt">Type</Typography>
            <Box sx={{ width: "calc(100% - 180px)" }}>
              <FormControl
                sx={{ width: "100% !important" }}
                error={formik.touched.type && Boolean(formik.errors.type)}
              >
                <Select
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="Validation">Validation</MenuItem>
                  <MenuItem value="Assignment">Assignment</MenuItem>
                  <MenuItem value="Rejection">Rejection</MenuItem>
                  <MenuItem value="QC">QC</MenuItem>
                  <MenuItem value="Internal">Internal</MenuItem>
                </Select>
                {formik.touched.type && Boolean(formik.errors.type) && (
                  <FormHelperText>This field is required.</FormHelperText>
                )}
              </FormControl>
            </Box>
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="label-txt">Description</Typography>
            <TextField
              id="description"
              name="description"
              fullWidth
              multiline
              rows={10}
              value={formik.values.description}
              onChange={formik.handleChange}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
          </Box>
        </form>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          sx={styles.save}
          variant="contained"
          autoFocus
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid}
        >
          Save
        </Button>
      </DialogActions>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
      {loading && <Loading />}
    </Dialog>
  );
}
